import { RadialCardConfig, CardData } from '../types'

interface RadialProgressProps {
  config: RadialCardConfig
  data: CardData
}

export function RadialProgress({ config, data }: RadialProgressProps) {
  const value = data.value ?? 0
  const total = data.total ?? 100
  const ratio = total > 0 ? value / total : 0
  const progressStroke =
    config.color ??
    (ratio <= 0.33 ? 'hsl(var(--error))' : ratio <= 0.66 ? 'hsl(var(--warning))' : 'hsl(var(--success))')

  // Calculate percentage
  const percentage = Math.min(100, Math.max(0, (value / total) * 100))

  // SVG parameters - using relative sizing for better container fitting
  const viewBoxSize = 100
  const radius = viewBoxSize / 2
  const strokeWidth = viewBoxSize / 15
  const normalizedRadius = radius - strokeWidth / 2

  // Angle calculation for slightly more than half circle (210 degrees)
  const startAngle = -195 * (Math.PI / 180) // Start at -195 degrees (converted to radians)
  const endAngle = 15 * (Math.PI / 180) // End at 15 degrees (converted to radians)

  // Calculate the lowest point of the arc to trim viewBox height
  // Adding even more padding to ensure the entire stroke is visible
  const lowestPointY = radius + normalizedRadius * Math.sin(0) + strokeWidth * 2.5

  // Calculate arc path
  const createArcPath = () => {
    const start = {
      x: radius + normalizedRadius * Math.cos(startAngle),
      y: radius + normalizedRadius * Math.sin(startAngle),
    }

    const end = {
      x: radius + normalizedRadius * Math.cos(endAngle),
      y: radius + normalizedRadius * Math.sin(endAngle),
    }

    // Flag for arc greater than 180 degrees
    const largeArcFlag = 1

    return `M ${start.x},${start.y} A ${normalizedRadius},${normalizedRadius} 0 ${largeArcFlag} 1 ${end.x},${end.y}`
  }

  const arcPath = createArcPath()
  const arcLength = normalizedRadius * ((210 * Math.PI) / 180) // 210 degrees in radians * radius
  const progressOffset = arcLength - (percentage / 100) * arcLength

  // Calculate aspect ratio based on actual arc dimensions
  const aspectRatio = viewBoxSize / lowestPointY

  return (
    <div className="relative flex w-full flex-col items-center justify-center">
      {/* SVG container - only contains the progress arcs */}
      <div className="relative w-1/2" style={{ aspectRatio: aspectRatio }}>
        <svg
          className="h-full w-full"
          viewBox={`0 0 ${viewBoxSize} ${lowestPointY}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`Progress: ${value} out of ${total}`}
        >
          {/* Light gray background path */}
          <path d={arcPath} className="fill-none stroke-muted" strokeWidth={strokeWidth} strokeLinecap="round" />

          {/* Colored progress path */}
          <path
            d={arcPath}
            className="fill-none"
            stroke={progressStroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={progressOffset}
          />
        </svg>

        {/* Text content in divs, positioned absolutely */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/3 text-center">
          <div className="text-4xl font-bold text-foreground">{value}</div>
          <div className="mt-1 text-xs leading-tight text-muted-foreground">out of {total}</div>
        </div>
      </div>
    </div>
  )
}
